import ButtonVariants from "@core-components/button/ButtonVariants";
import Icon from "@core-components/icon/Icon";
import EPublicRoutes, { EPrivateRoutes } from "@core-types/routes.enum";
import { useAppNavigate } from "@hooks/language/useAppNavigate";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { EBreakpoints, useBreakpoint } from "@hooks/useBreakpoint";
import { Button, Menu } from "@mantine/core";
import Coins, { ECoinVariant } from "@modules/clientDashboard/components/Coins";
import useLoginModalStore, { ELoginModalState } from "@store/loginModal.store";
import useUserStore from "@store/user.store";
import { IconLogout } from "@tabler/icons-react";
import { type ReactNode, useCallback, useMemo } from "react";

const LoginNavbarButton: React.FC = () => {
  const { open } = useLoginModalStore();
  const { isLogin, logout } = useUserStore();
  const { t } = useAppTranslation("components.layout.sidebar");
  const { navigate } = useAppNavigate();
  const { breakpoint } = useBreakpoint();

  const handleLogout = () => {
    logout();
    navigate(EPublicRoutes.HOME);
  };

  const menuItem = useCallback(
    (label: string, icon: ReactNode, href: string) => {
      const isCurrentHref = window.location.href.includes(href);

      return (
        <Menu.Item
          key={label}
          className="default-icon"
          classNames={{
            itemLabel: `transition ${
              isCurrentHref
                ? "text-secret-blue-light"
                : "text-primary-light-text"
            }`,
          }}
          leftSection={icon}
          onClick={() => navigate(href)}
        >
          {label}
        </Menu.Item>
      );
    },
    [navigate],
  );
  const menuItems = useMemo(
    () => [
      menuItem(t("orders"), <Icon.Bag size={14} />, EPrivateRoutes.ORDERS),
      menuItem(
        t("rewards"),
        <Coins variant={ECoinVariant.REWARDS} size={15} />,
        EPrivateRoutes.REWARDS,
      ),
      menuItem(
        t("fidelity"),
        <Coins variant={ECoinVariant.FIDELITY} size={15} />,
        EPrivateRoutes.FIDELITY,
      ),
      menuItem(
        t("referral"),
        <Coins variant={ECoinVariant.AFFILIATE} size={15} />,
        EPrivateRoutes.AFFILIATION,
      ),
      menuItem(t("help"), <Icon.LifeBuoy size={14} />, EPrivateRoutes.HELP),
    ],
    [menuItem, t],
  );

  if (!isLogin)
    return (
      <ButtonVariants.ActionIcon onClick={() => open(ELoginModalState.LOGIN)}>
        <Icon.Login />
      </ButtonVariants.ActionIcon>
    );

  return (
    <>
      {breakpoint > EBreakpoints.MD && (
        <>
          <Button
            leftSection={<Icon.Bag size={20} />}
            size="lg"
            className="default-icon hover-scale font-inter text-[14px]"
            radius={12}
            onClick={() => navigate(EPrivateRoutes.ORDERS)}
          >
            {t("orders")}
          </Button>

          <ButtonVariants.ActionIcon
            onClick={() => navigate(EPrivateRoutes.FIDELITY)}
          >
            <Coins variant={ECoinVariant.SECRET_SMURFS} size={25} />
          </ButtonVariants.ActionIcon>
        </>
      )}

      <Menu shadow="md" width={200} trigger="click-hover">
        <Menu.Target>
          <ButtonVariants.ActionIcon
            onClick={() => open(ELoginModalState.LOGIN)}
          >
            <Icon.Login />
          </ButtonVariants.ActionIcon>
        </Menu.Target>

        <Menu.Dropdown
          classNames={{
            dropdown: "bg-primary-light border-none rounded-[8px] px-2 py-4",
          }}
        >
          {menuItems}

          <Menu.Divider className="border-primary-lighter" />

          <Menu.Item
            onClick={handleLogout}
            className="default-icon"
            classNames={{
              itemLabel: "text-danger-default",
            }}
            leftSection={
              <IconLogout size={14} className="text-danger-default" />
            }
          >
            {t("logout")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  );
};

export default LoginNavbarButton;
